/**
 * Shared registry of hit-rects from the Pixi pod-room scene, read by
 * the DOM tooltip overlay (``PixiTooltipLayer``).
 *
 * Why a module-level singleton?  Pixi draws the agent characters in
 * canvas-space; React's DOM tooltip layer needs to know *where* each
 * character lives to translate ``mousemove`` events into "user is
 * hovering Quant" decisions.  We could plumb the layout through props
 * or the Zustand store, but:
 *
 *   * Props would require lifting the scene ref up through three
 *     components (PodRoomPixiView -> PixiCanvas -> SceneManager).
 *   * The Zustand store would trigger a React re-render every time
 *     the scene re-lays out (resize / pod swap), which is wasteful --
 *     hover hit-tests are pull-based, not push-based.
 *
 * A module-level ref keeps things simple: scene writes on layout,
 * overlay reads on mousemove.  The dashboard only shows one pod room
 * at a time (the room view replaces the building view), so the
 * singleton pattern matches reality.
 *
 * Coordinates are in CSS pixels relative to the host ``<div>`` that
 * contains the Pixi canvas.  ``PixiCanvas`` uses ``autoDensity`` so
 * the Pixi world's pixel-space already matches the host's CSS-pixel
 * space; no DPI conversion needed in the overlay.
 */

export interface AgentHitRect {
  /** Stable id: ``"qa"`` / ``"sa"`` / ``"fa"`` / ``"researcher"`` /
   *  ``"rm"`` / ``"ea"`` / ``"pm"``.  Matches the ``actor`` field on
   *  ``agent.*`` events so the overlay can look up the agent's state
   *  in the Zustand store. */
  actor: string;
  /** Human-readable label (``"Quant"``, ``"Portfolio Manager"``, ...).
   *  Used as the tooltip header. */
  label: string;
  /** Center coordinates in host-relative CSS pixels. */
  cx: number;
  cy: number;
  /** Radius of the circular hit region.  We use circles (not bboxes)
   *  because the character glyphs are round and a hit-test on a
   *  square would cover dead corners. */
  radius: number;
}

let _agentRects: ReadonlyArray<AgentHitRect> = [];

/** Called by the Pixi scene after every layout.  Replaces the
 * registry atomically so the overlay never observes a partially
 * updated list. */
export function setPodRoomAgentRects(rects: ReadonlyArray<AgentHitRect>): void {
  _agentRects = rects;
}

export function getPodRoomAgentRects(): ReadonlyArray<AgentHitRect> {
  return _agentRects;
}

/** Clears the registry; used when leaving the pod-room view so the
 * overlay doesn't hold stale rects from a no-longer-visible scene. */
export function clearPodRoomAgentRects(): void {
  _agentRects = [];
}

/** Find the agent whose circular hit area contains ``(x, y)`` (CSS
 * pixels, host-relative).  Returns ``null`` for misses.  When multiple
 * agents would match (overlapping circles, e.g. PM crown over agent),
 * we return the *closest by center distance* -- intuitive at edges. */
export function hitTestAgent(x: number, y: number): AgentHitRect | null {
  let best: AgentHitRect | null = null;
  let bestDist = Infinity;
  for (const rect of _agentRects) {
    const dx = x - rect.cx;
    const dy = y - rect.cy;
    const dist = Math.hypot(dx, dy);
    if (dist <= rect.radius && dist < bestDist) {
      best = rect;
      bestDist = dist;
    }
  }
  return best;
}
